import { InvitationStatus } from "@garther/interfaces";
import { Spinner } from "@garther/ui/components";
import { useNavigate, useParams } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthStore } from "../../stores/auth.store";
import { useInvitationStore } from "../../stores/invitation.store";
import { useQuizStore } from "../../stores/quiz.store";
import { Layout } from "../layout/layout";
import { FriendInvitationAuth } from "./friend-invitation-auth";
import { FriendInvitationConfirm } from "./friend-invitation-confirm";
import { FriendInvitationError } from "./friend-invitation-error";
import { FriendInvitationSuccess } from "./friend-invitation-success";

export const FriendInvitationPage: React.FC = () => {
  const { t } = useTranslation();
  const { invitationCode } = useParams({ from: "/invite/$invitationCode" });
  const navigate = useNavigate();
  const { loggedIn, curUser } = useAuthStore();
  const { currentQuiz } = useQuizStore();
  const { currentInvitation, getInvitationByCodeWithDetails, isLoading, error } = useInvitationStore();

  const [step, setStep] = useState<"loading" | "auth" | "quiz" | "confirm" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  console.log("[DEBUG] FriendInvitationPage", invitationCode, currentInvitation, loggedIn, currentQuiz, step);
  useEffect(() => {
    loadInvitation();
  }, [invitationCode]);

  useEffect(() => {
    console.log("[DEBUG] useEffect", currentInvitation, loggedIn, currentQuiz, invitationCode);
    if (currentInvitation) {
      determineNextStep();
    }
  }, [currentInvitation, loggedIn, currentQuiz]);

  const loadInvitation = async () => {
    if (!invitationCode) {
      setErrorMessage(t("friendInvitation.invitationNotFound"));
      setStep("error");
      return;
    }

    const { invitation } = await getInvitationByCodeWithDetails(invitationCode);
    if (!invitation) {
      setErrorMessage(t("friendInvitation.invitationNotFound"));
      setStep("error");
      return;
    }

    // Check if invitation is expired
    if (invitation.expiresAt < new Date()) {
      setErrorMessage(t("friendInvitation.invitationExpired"));
      setStep("error");
      return;
    }

    // Check if invitation is already used
    if (invitation.status !== InvitationStatus.PENDING) {
      if (invitation.status === InvitationStatus.CONFIRMED) {
        setErrorMessage(t("friendInvitation.invitationAlreadyUsed"));
      } else {
        setErrorMessage(t("friendInvitation.invitationAlreadyUsed"));
      }
      setStep("error");
      return;
    }
  };

  const determineNextStep = () => {
    console.log("[DEBUG] determineNextStep", currentInvitation, loggedIn, currentQuiz);
    if (!currentInvitation) return;

    // If user is not logged in, show auth
    if (!loggedIn) {
      setStep("auth");
      return;
    }

    // If user is logged in but hasn't completed quiz, show quiz
    if (!currentQuiz?.completed) {
      setStep("quiz");
      return;
    }

    // If user is logged in and has completed quiz, show confirmation
    setStep("confirm");
  };

  const handleAuthSuccess = () => {
    setStep("loading");
    // The useEffect will determine the next step based on quiz completion
  };

  const handleConfirmSuccess = () => {
    setStep("success");
  };

  const handleError = (message: string) => {
    setErrorMessage(message);
    setStep("error");
  };

  if (isLoading || step === "loading") {
    return (
      <Layout>
        <div className="min-h-screen e-center">
          <div className="text-center">
            <Spinner className="mx-auto mb-4" />
            <p className="text-text-secondary">{t("common.loading")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (step === "error") {
    return (
      <Layout>
        <FriendInvitationError message={errorMessage || error || t("common.error")} />
      </Layout>
    );
  }

  if (step === "auth") {
    return (
      <Layout>
        <FriendInvitationAuth onSuccess={handleAuthSuccess} onError={handleError} />
      </Layout>
    );
  }

  if (step === "quiz") {
    // HACK: curUser is not guaranteed to be logged in, so we need to use ! to bypass the type check
    // curUser is guaranteed to be logged in because of the useEffect in the parent component
    navigate({
      to: "/user/$userId/quiz",
      params: { userId: curUser!.fbUser!.uid },
      search: { redirectUrl: `${window.location.origin}/invite/${invitationCode}` }
    });
  }

  if (step === "confirm") {
    return (
      <Layout>
        {currentInvitation && (
          <FriendInvitationConfirm
            invitation={currentInvitation}
            onSuccess={handleConfirmSuccess}
            onError={handleError}
          />
        )}
      </Layout>
    );
  }

  if (step === "success") {
    return <Layout>{currentInvitation && <FriendInvitationSuccess invitation={currentInvitation} />}</Layout>;
  }

  return null;
};
